import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  useEffect(() => {
    fetchVideos();
  }, [token]);

  const fetchVideos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/videos`, { headers });
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-12">
          <h1 
            data-testid="videos-heading"
            className="text-4xl sm:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
          >
            Featured Videos
          </h1>
          <p className="text-xl text-slate-400 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Subscribe for exclusive content
          </p>
          <div className="flex gap-4">
            <Link to="/pricing">
              <Button className="bg-red-600 hover:bg-red-700 text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Subscribe for Premium Access
              </Button>
            </Link>
            <a href="https://youtube.com/@unfilteredvisionnews" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Visit YouTube Channel
              </Button>
            </a>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-slate-800 aspect-video rounded-sm mb-4"></div>
                <div className="h-4 bg-slate-800 rounded mb-2"></div>
                <div className="h-3 bg-slate-800 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-xl text-slate-400 mb-8">No videos available yet. Check back soon!</p>
            <Link to="/">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Return Home
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {videos.map((video) => (
              <div
                key={video.id}
                data-testid={`video-card-${video.id}`}
                className="bg-slate-900 rounded-sm border border-slate-800 overflow-hidden"
              >
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${video.youtube_id}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="p-4">
                  <h3 
                    className="text-lg font-bold mb-2 line-clamp-2"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    {video.title}
                  </h3>
                  <p className="text-sm text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatDate(video.published_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideosPage;
